import { useMemo, useState } from "react";
import { Building2, ChevronUp } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency, formatNumber } from "@/utils/format";
import { CentroCustoRecebimentoStat } from "../chartUtils";
import { ModalEntityTitle, IncomeTable, SummaryBanner } from "./FinanceiroModalContent";
import { cn } from "@/utils/cn";

/** Cores por posição no ranking (mesmo visual dos Pagamentos). */
const BAR_COLORS = [
  "#9D174D",
  "#B03858",
  "#E879A8",
  "#2563EB",
  "#7C3AED",
  "#C4B5FD",
  "#3B82F6",
  "#38BDF8",
  "#BFDBFE",
  "#64748B",
];

function buildAxis(maxValor: number): { axisMax: number; ticks: number[] } {
  if (maxValor <= 0) {
    return { axisMax: 160_000, ticks: [0, 40_000, 80_000, 120_000, 160_000] };
  }
  const step = Math.max(10_000, Math.ceil(maxValor / 4 / 5000) * 5000);
  const axisMax = step * 4;
  const ticks = [0, step, step * 2, step * 3, step * 4];
  return { axisMax, ticks };
}

function formatTick(v: number): string {
  if (v <= 0) return "0k";
  return `${Math.round(v / 1000)}k`;
}

interface Props {
  data: CentroCustoRecebimentoStat[];
  loading: boolean;
}

export function RecebimentosPorCentro({ data, loading }: Props) {
  const [selected, setSelected] = useState<CentroCustoRecebimentoStat | null>(null);

  const { axisMax, ticks } = useMemo(() => {
    const maxValor = data.reduce((s, d) => Math.max(s, d.valor), 0);
    return buildAxis(maxValor);
  }, [data]);

  return (
    <>
      <div className="flex h-full min-h-0 flex-col">
        <ChartContainer
          title="Recebimentos por Centro de Custos"
          subtitle="Total recebido no período · Ranking completo · % sobre o total recebido"
          titleClassName="text-base font-bold text-slate-900 tracking-tight"
          subtitleClassName="text-sm text-slate-500 font-normal"
          loading={loading}
          empty={!loading && data.length === 0}
          height="auto"
          contentStretch
          className="flex h-full min-h-0 flex-col gap-3 p-4"
          headerAction={
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                Ranking
              </span>
              <span
                className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm"
                aria-hidden
              >
                <ChevronUp className="h-3.5 w-3.5 stroke-[2.5]" />
              </span>
            </div>
          }
        >
          <div className="flex min-h-0 flex-1 flex-col gap-0 pb-0.5">
          {/* Linha do R$ + faixa do gráfico alinhada às barras */}
          <div className="flex min-w-0 shrink-0 gap-2 sm:gap-3">
            <div className="w-8 shrink-0" aria-hidden />
            <div className="w-[132px] shrink-0 sm:w-[148px]" aria-hidden />
            <div className="relative min-h-5 min-w-0 flex-1">
              <span className="absolute right-1 top-0 text-[11px] font-medium text-slate-400">R$</span>
            </div>
          </div>

          <div className="min-h-0 max-h-[280px] flex-1 overflow-y-auto overscroll-y-contain [scrollbar-gutter:stable] pr-0.5 sm:max-h-[320px]">
          {data.map((row, idx) => {
            const rank = idx + 1;
            const barPct = axisMax > 0 ? Math.min(100, (row.valor / axisMax) * 100) : 0;
            const sharePct = row.pct ?? 0;
            const barColor = BAR_COLORS[(rank - 1) % BAR_COLORS.length];

            return (
              <button
                key={`${row.name}-${idx}`}
                type="button"
                onClick={() => setSelected(row)}
                className={cn(
                  "flex w-full min-w-0 gap-2 rounded-lg py-1.5 pl-0.5 pr-1 text-left transition-colors hover:bg-slate-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 sm:gap-3",
                  idx < 2 && "bg-sky-50/80 hover:bg-sky-50",
                )}
              >
                <div className="flex w-8 shrink-0 justify-center pt-0.5">
                  <span
                    className={cn(
                      "flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold tabular-nums leading-none",
                      rank <= 3 ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-600",
                    )}
                  >
                    {String(rank).padStart(2, "0")}
                  </span>
                </div>
                <div
                  className={cn(
                    "w-[132px] shrink-0 truncate pt-0.5 text-xs leading-snug sm:w-[148px] sm:text-[13px]",
                    rank <= 3 ? "font-bold text-slate-900" : "font-normal text-slate-500",
                  )}
                  title={row.name}
                >
                  {row.name}
                </div>
                <div className="flex min-w-0 flex-1 items-center gap-1.5 pt-0.5 sm:gap-2">
                  <div className="relative h-6 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="pointer-events-none absolute inset-y-0 left-0 flex w-full"
                      aria-hidden
                    >
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="flex-1 border-r border-slate-200/95 last:border-r-0"
                        />
                      ))}
                    </div>
                    <div
                      className="relative z-[1] h-full rounded-full shadow-sm"
                      style={{
                        width: `${barPct}%`,
                        backgroundColor: barColor,
                        minWidth: barPct > 0 ? "6px" : undefined,
                      }}
                    />
                  </div>
                  <span
                    className="w-[108px] shrink-0 text-right text-[11px] tabular-nums sm:w-[118px] sm:text-xs"
                    title={`${formatNumber(sharePct, 1)}% do total recebido no período`}
                  >
                    <span className="font-semibold text-slate-800">{formatCurrency(row.valor, true)}</span>
                    <span className="ml-1 font-medium text-slate-500">{formatNumber(sharePct, 1)}%</span>
                  </span>
                </div>
              </button>
            );
          })}
          </div>

          {/* Eixo X */}
          <div className="mt-1 flex min-w-0 shrink-0 gap-2 border-t border-slate-100 pt-1.5 sm:gap-3">
            <div className="w-8 shrink-0" aria-hidden />
            <div className="w-[132px] shrink-0 sm:w-[148px]" aria-hidden />
            <div className="flex min-w-0 flex-1 justify-between px-0 text-[11px] tabular-nums text-slate-400">
              {ticks.map((t) => (
                <span key={t}>{formatTick(t)}</span>
              ))}
            </div>
            <div className="w-[108px] shrink-0 sm:w-[118px]" aria-hidden />
          </div>
          </div>
        </ChartContainer>
      </div>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size="xl"
        title={
          selected && (
            <ModalEntityTitle
              icon={<Building2 className="w-4 h-4" />}
              label={selected.name}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-700"
            />
          )
        }
      >
        {selected && (
          <>
            <SummaryBanner
              label="Total recebido"
              value={formatCurrency(selected.valor)}
              color="#059669"
              subtitle={`${selected.quantidade} título${selected.quantidade !== 1 ? "s" : ""}${selected.pct != null ? ` · ${formatNumber(selected.pct, 1)}% do total recebido` : ""}`}
            />
            <IncomeTable items={selected.items} />
          </>
        )}
      </Modal>
    </>
  );
}
